import React, { forwardRef, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Checkbox } from '../checkbox';

import {
  DropdownRootStyled,
  DropdownTriggerStyled,
  DropdownOptionsStyled,
  DropdownTitleStyled,
  DropdownItemStyled,
  DropdownSeparatorStyled,
} from './style';

// Forward ref version of DropdownRoot with click-outside behavior
const DropdownRootWithRef = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof DropdownRootStyled> & {
    onClose?: () => void;
    isOpen?: boolean;
  }
>(({ onClose, isOpen, ...props }, ref) => {
  const internalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !onClose) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (internalRef.current && !internalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return <DropdownRootStyled ref={ref || internalRef} {...props} />;
});

interface DropdownComposite {
  Root: typeof DropdownRootWithRef;
  Trigger: typeof DropdownTriggerStyled;
  Options: typeof DropdownOptionsStyled;
  Title: typeof DropdownTitleStyled;
  Item: typeof DropdownItemStyled;
  Separator: typeof DropdownSeparatorStyled;
}

const Dropdown: DropdownComposite = {
  Root: DropdownRootWithRef,
  Trigger: DropdownTriggerStyled,
  Options: DropdownOptionsStyled,
  Title: DropdownTitleStyled,
  Item: DropdownItemStyled,
  Separator: DropdownSeparatorStyled,
};

export { Dropdown };

// Container for the dropdown
const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  background-color: ${({ theme }) => theme.dropdown.optionBackgroundColor};
  border: 1px solid ${({ theme }) => theme.dropdown.dividerColor};
  border-radius: 4px;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.15);
  width: 151px;
  padding: 0px 0px 4px;
  margin-top: 4px;
`;

// Individual menu header component
const MenuHeaderContainer = styled.div`
  padding: 8px 0px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.dropdown.dividerColor};
`;

const MenuHeaderText = styled.div`
  font-family: 'Open Sans';
  font-weight: 700;
  font-size: 9px;
  line-height: 1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.dropdown.titleTextColor};
  padding: 0px 9px 6px;
`;

// Individual menu item component
const MenuItemContainer = styled.div`
  padding: 4px 8px;
  cursor: pointer;
  font-family: 'Open Sans';
  font-weight: 400;
  font-size: 11px;
  line-height: 1.1818em;
  color: ${({ theme }) => theme.dropdown.optionTextColor};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.dropdown.itemHoverBackgroundColor};
  }
`;

// Separator between groups
const MenuSeparatorContainer = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.dropdown.dividerColor};
  margin: 4px 0;
`;

export interface DropdownItem {
  id: string;
  label: string;
  onClick: () => void;
}

export interface DropdownGroup {
  id: string;
  headerText: string;
  items: DropdownItem[];
}

interface DropdownProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const DropdownV2: React.FC<DropdownProps> = ({ className, style, children }) => (
  <DropdownContainer className={className} style={style}>
    {children}
  </DropdownContainer>
);

// Menu header component
interface MenuHeaderProps {
  children: React.ReactNode;
}

export const MenuHeader: React.FC<MenuHeaderProps> = ({ children }) => (
  <MenuHeaderContainer>
    <MenuHeaderText>{children}</MenuHeaderText>
  </MenuHeaderContainer>
);

// Menu item component
interface MenuItemProps {
  onClick: () => void;
  children: React.ReactNode;
  checkbox?: {
    checked: boolean;
    onChange: () => void;
  };
}

export const MenuItem: React.FC<MenuItemProps> = ({ onClick, children, checkbox }) => (
  <MenuItemContainer onClick={onClick}>
    {checkbox && <Checkbox checked={checkbox.checked} onChange={checkbox.onChange} />}
    {children}
  </MenuItemContainer>
);
// Menu separator component
export const MenuSeparator: React.FC = () => <MenuSeparatorContainer />;
